import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';

interface SnapshotItem {
  itemId: number;
  itemName: string;
  unit: string;
  quantityIn: number;
  quantityOut: number;
  stockAtDate: number;
}

interface SnapshotResultTableProps {
  items: SnapshotItem[];
  snapshotDate: string;
  warehouseName: string;
  warehouseCode: string;
  onExport: () => void;
  isExporting?: boolean;
}

const SnapshotResultTable: React.FC<SnapshotResultTableProps> = ({
  items,
  snapshotDate,
  warehouseName,
  warehouseCode,
  onExport,
  isExporting,
}) => {
  const { t } = useTranslation();

  const formatUnit = (unit: string): string => {
    return t(`items.units.${unit}`, { defaultValue: unit });
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('vi-VN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {t('warehouse.snapshot.stockAtDate')} {new Date(snapshotDate).toLocaleDateString()}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {warehouseName} ({warehouseCode}) - {items.length} {t('warehouse.snapshot.totalItems')}
          </p>
        </div>
        <button
          onClick={onExport}
          disabled={isExporting}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          {isExporting ? t('warehouse.snapshot.exporting') : t('warehouse.snapshot.exportSnapshot')}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('warehouse.snapshot.stt')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('warehouse.snapshot.product')}
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('warehouse.snapshot.unitCol')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('warehouse.snapshot.inCol')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('warehouse.snapshot.outCol')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('warehouse.snapshot.stockAtDateCol')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  {t('warehouse.snapshot.noData')}
                </td>
              </tr>
            ) : (
              items.map((item, index) => (
                <tr key={item.itemId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {item.itemName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                    {formatUnit(item.unit)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatNumber(item.quantityIn)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatNumber(item.quantityOut)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-right ${
                    item.stockAtDate < 0 ? 'text-red-600' : 'text-gray-900'
                  }`}>
                    {formatNumber(item.stockAtDate)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {items.length > 0 && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            <strong>{t('warehouse.snapshot.total')}:</strong> {items.length} {t('warehouse.snapshot.totalItems')}
          </p>
        </div>
      )}
    </div>
  );
};

export default SnapshotResultTable;
